import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  rsvpToEvent,
  getMyRsvps, getUserRsvp, getSharedEvent,
  getSharedEventBySlug, getSharedEventByToken,
} from '../services/sharing.service';

const useShareStore = create(
  persist(
    (set, get) => ({
      // Cache of public events by slug
      publicEvents: {},

      // Current user's RSVPs: { [eventId]: 'going' | 'not_going' }
      myRsvps: {},

      // Enriched event objects for "My RSVPs" view
      myRsvpEvents: [],

      // Loading states
      isLoadingEvent: false,
      isRsvping: false,
      isLoadingMyRsvps: false,

      // Placeholder for future phases
      mySubscriptions: {},    // Phase 5
      notifications: [],      // Phase 6
      unreadCount: 0,         // Phase 6

      // --- Actions ---

      fetchPublicEvent: async (slug) => {
        set({ isLoadingEvent: true });
        try {
          const event = await getSharedEventBySlug(slug);
          if (event) {
            set(state => ({
              publicEvents: { ...state.publicEvents, [slug]: event },
              isLoadingEvent: false,
            }));
          } else {
            set({ isLoadingEvent: false });
          }
          return event;
        } catch (error) {
          set({ isLoadingEvent: false });
          throw error;
        }
      },

      fetchInviteEvent: async (token) => {
        set({ isLoadingEvent: true });
        try {
          const event = await getSharedEventByToken(token);
          if (event && event.slug) {
            set(state => ({
              publicEvents: { ...state.publicEvents, [event.slug]: event },
              isLoadingEvent: false,
            }));
          } else {
            set({ isLoadingEvent: false });
          }
          return event;
        } catch (error) {
          set({ isLoadingEvent: false });
          throw error;
        }
      },

      rsvp: async (eventId, status) => {
        const previousStatus = get().myRsvps[eventId] || null;

        // Optimistic update
        set(state => ({
          myRsvps: { ...state.myRsvps, [eventId]: status },
          isRsvping: true,
        }));

        try {
          await rsvpToEvent(eventId, status);
          set({ isRsvping: false });

          // Background refetch to get updated counts
          const { publicEvents } = get();
          const slug = Object.keys(publicEvents).find(
            s => publicEvents[s]?.id === eventId
          );
          if (slug) {
            get().fetchPublicEvent(slug);
          }
        } catch (error) {
          // Rollback on error
          set(state => ({
            myRsvps: { ...state.myRsvps, [eventId]: previousStatus },
            isRsvping: false,
          }));
          throw error;
        }
      },

      cancelRsvp: async (eventId) => {
        return get().rsvp(eventId, 'not_going');
      },

      fetchUserRsvp: async (eventId) => {
        try {
          const rsvp = await getUserRsvp(eventId);
          if (rsvp) {
            set(state => ({
              myRsvps: { ...state.myRsvps, [eventId]: rsvp.status },
            }));
          }
          return rsvp;
        } catch {
          return null;
        }
      },

      fetchMyRsvps: async () => {
        set({ isLoadingMyRsvps: true });
        try {
          const result = await getMyRsvps({ limit: 100 });
          const rsvpMap = {};
          result.rsvps.forEach(r => {
            rsvpMap[r.eventId || r.id] = r.status;
          });
          set({ myRsvps: rsvpMap, isLoadingMyRsvps: false });
        } catch (error) {
          set({ isLoadingMyRsvps: false });
          throw error;
        }
      },

      fetchMyRsvpEvents: async () => {
        const { myRsvps } = get();
        const goingEventIds = Object.entries(myRsvps)
          .filter(([, status]) => status === 'going')
          .map(([eventId]) => eventId);

        if (goingEventIds.length === 0) {
          set({ myRsvpEvents: [] });
          return [];
        }

        try {
          const events = await Promise.all(
            goingEventIds.map(id => getSharedEvent(id))
          );
          const validEvents = events.filter(Boolean);
          set({ myRsvpEvents: validEvents });
          return validEvents;
        } catch (error) {
          console.error('Failed to fetch RSVP events:', error);
          return [];
        }
      },

      // Reset store (on logout)
      clearUserData: () => set({
        publicEvents: {},
        myRsvps: {},
        myRsvpEvents: [],
        mySubscriptions: {},
        notifications: [],
        unreadCount: 0,
        isLoadingEvent: false,
        isRsvping: false,
        isLoadingMyRsvps: false,
      }),
    }),
    {
      name: 'grownups-share-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        myRsvps: state.myRsvps,
      }),
    }
  )
);

export default useShareStore;
